import router from '@/router'
import { CategoryService } from '@/services/category.service'
import { ProductService } from '@/services/product.service'
import { UserService } from '@/services/user.service'
import { SupplierService } from '@/services/supplier.service'
import { PriceService } from '@/services/price.service'
import { WarehouseService } from '@/services/warehouse.service'
import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import { mapDealerOption, commitPaginationFromMeta, showErrorToast } from './helpers'

export const catalogActions = {
  async loadCategories({ commit }) {
    const response = await CategoryService.categories()
    commit('SET_CATEGORIES', response.data.data)
  },

  async loadCategoriesForSelect({ commit }) {
    const response = await CategoryService.categoriesForSelect()
    commit('SET_CATEGORIES_SELECT', response.data.data)
  },

  async loadCategoryById({ commit }, id) {
    const response = await CategoryService.category(id)
    commit('SET_CATEGORY', response.data.data)
  },

  async saveCategory({ commit, state }, data) {
    try {
      const payload = {
        active: data.active,
        parent_id: data.parent_id,
        name: data.name,
        link: data.link,
        desc: data.desc,
        groups: data.groups
      }

      if (data.image !== undefined) {
        payload.image = data.image
      }

      const response = await CategoryService.saveCategory(payload, data.id)
      const status = response?.status

      if ([HTTP_STATUS_CODES.OK, HTTP_STATUS_CODES.CREATED].includes(status)) {
        router.push({ name: 'categories_list' })
      }

      return response
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async storeCategory({ commit }, data) {
    try {
      const response = await CategoryService.storeCategory(data)
      if (response) {
        router.push({ name: 'categories_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async removeCategory({ commit }, id) {
    try {
      const response = await CategoryService.removeCategory(id)
      if (response) {
        router.push({ name: 'categories_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async orderCategories(_, data) {
    try {
      const response = await CategoryService.reorderCategories({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadProducts({ commit }, data) {
    const response = await ProductService.products(data)
    commit('SET_PRODUCTS', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadProductsFiltersData({ commit }) {
    try {
      const response = await ProductService.filtersData()
      const payload = response?.data?.data || response?.data || {}
      const categories = Array.isArray(payload.categories) ? payload.categories : payload.category
      const suppliers = Array.isArray(payload.suppliers) ? payload.suppliers : payload.supplier
      const mapToSelectOption = (item) => ({
        value: item.value ?? item.id,
        label: item.label ?? item.name
      })

      commit('SET_CATEGORIES_SELECT', Array.isArray(categories) ? categories.map(mapToSelectOption) : [])
      commit('SET_SUPPLIERS_SELECT', Array.isArray(suppliers) ? suppliers.map(mapToSelectOption) : [])
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadFilteredProducts({ commit }, data) {
    const response = await ProductService.filterProducts(data)
    if (response.data.data) {
      commit('SET_PRODUCTS', response.data.data)
      commitPaginationFromMeta(commit, response)
    } else {
      commit('SET_PRODUCTS', [])
      commit('SET_PAGINATION', [])
    }
  },

  async loadImportedProducts({ commit }, data) {
    const response = await ProductService.importedProducts(data)
    commit('SET_IMPORTED_PRODUCTS', response.data.data)
    commit('SET_IMPORTED_PRODUCTS_TOTAL', response.data.meta.total)
    commitPaginationFromMeta(commit, response)
  },

  async importProducts({ commit }, products) {
    commit('SET_IMPORTED_PRODUCTS', [])
    await ProductService.importProducts(products)
  },

  async loadProductsSelect({ commit }, withHashId = false) {
    const response = await ProductService.productsSelect(withHashId)
    commit('SET_PRODUCTS_SELECT', response.data.data)
  },

  async loadProductsCategory({ commit }, id) {
    const response = await ProductService.productsCategory(id)
    commit('SET_PRODUCTS', response.data.data)
  },

  async loadProductById({ commit }, id) {
    const response = await ProductService.product(id)
    const productData = response?.data?.data ?? response?.data
    commit('SET_PRODUCT', productData)
    const statuses = productData?.statuses ?? []
    if (Array.isArray(statuses) && statuses.length > 0) {
      commit('SET_PRODUCT_STATUSES_SELECT', statuses)
    }
  },

  async saveProduct({ dispatch }, { data, isGlobal }) {
    try {
      const response = isGlobal
        ? await ProductService.saveProductGlobalLocal({ ...data }, data.id)
        : await ProductService.saveProduct({ ...data }, data.id)
      const status = response?.status || response?.response?.status
      const isSuccess = [HTTP_STATUS_CODES.OK, HTTP_STATUS_CODES.CREATED].includes(status)

      if (isSuccess) {
        dispatch('loadProductById', data.id)
        dispatch('loadWarehouses', {
          page: 1,
          productId: data.id
        })
      }

      return response
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async newProduct({ commit, state }, data) {
    try {
      const response = await ProductService.newProduct({
        ...data,
        ...{ categories: state.categoriesModel }
      })

      return response
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadWarehouses({ commit }, data) {
    try {
      const response = await WarehouseService.warehouses(data)

      commit('SET_WAREHOUSES', response.data.data)
      commitPaginationFromMeta(commit, response)
      commit('SET_WAREHOUSES_COUNT', response.data.meta.total)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async updateQuantity(_, data) {
    try {
      const response = await WarehouseService.updateWarehouse(data.id, data)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async newWarehouse(_, data) {
    try {
      const response = await WarehouseService.newStore({
        ...data
      })

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadVendors({ commit }) {
    const response = await UserService.vendors()
    commit('SET_VENDOR_LIST', response.data.data)
  },

  async loadVendorById({ commit }, id) {
    const response = await UserService.vendor(id)
    commit('SET_VENDOR', response.data.data)
  },

  async loadSuppliers({ commit }) {
    const response = await SupplierService.suppliers()
    commit('SET_SUPPLIERS_LIST', response.data.data)
  },

  async loadSupplierById({ commit }, id) {
    const response = await UserService.supplier(id)
    commit('SET_SUPPLIER', response.data.data)
  },

  async storeVendor({ commit }, data) {
    try {
      const response = await UserService.storeVendor(data)
      if (response) {
        router.push({ name: 'vendors_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteVendor({ commit }, id) {
    try {
      const response = await UserService.deleteVendor(id)
      if (response) {
        commit('REMOVE_VENDOR_LIST_ITEM', id)
        router.push({ name: 'vendors_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async saveVendor({ commit }, data) {
    try {
      const response = await UserService.saveVendor(data, data.id)
      if (response) {
        router.push({ name: 'vendors_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async storeSupplier({ commit }, data) {
    try {
      const response = await SupplierService.storeSupplier(data)
      if (response) {
        router.push({ name: 'suppliers_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deleteSupplier({ commit }, id) {
    try {
      const response = await SupplierService.deleteSupplier(id)
      if (response) {
        commit('REMOVE_SUPPLIER_LIST_ITEM', id)
        router.push({ name: 'suppliers_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async saveSupplier({ commit }, data) {
    try {
      const response = await SupplierService.saveSupplier(data, data.id)
      if (response) {
        router.push({ name: 'suppliers_list' })
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async loadPrices({ commit }, page) {
    const response = await PriceService.prices(page)
    commit('SET_PRICES', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadPriceById({ commit }, id) {
    const response = await PriceService.price(id)
    commit('SET_PRICE', response.data.data)
  },

  async loadSinglePriceListById({ commit }, id) {
    const response = await PriceService.singlePriceList(id)
    commit('SET_SINGLE_PRICE_LIST', response.data.data)
  },

  async updatePrice(_, data) {
    try {
      const response = await PriceService.updatePrice(data, data.get('id'))

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      } else {
        return response
      }
    } catch (e) {
      showErrorToast(e)
      return e
    }
  },

  async deletePrice(_, id) {
    try {
      const response = await PriceService.deletePriceById(id)

      if (response.status === HTTP_STATUS_CODES.OK) {
        return true
      }
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadPriceList({ commit }, page) {
    try {
      const response = await PriceService.priceList(page)
      commit('SET_PRICE_LIST', response.data.data)
      commitPaginationFromMeta(commit, response)
    } catch (err) {
      showErrorToast(err)
      return err
    }
  },

  async loadPriceCategories({ commit }) {
    const response = await PriceService.priceCategories()
    commit('SET_PRICE_CATEGORIES', response.data.data)
  },

  async loadBrochureCategories({ commit }) {
    const response = await PriceService.brochureCategories()
    commit('SET_BROCHURE_CATEGORIES', response.data.data)
  },

  async loadBrochures({ commit }, page) {
    const response = await PriceService.brochures(page)
    commit('SET_BROCHURES', response.data.data)
    commitPaginationFromMeta(commit, response)
  },

  async loadBrochureById({ commit }, id) {
    const response = await PriceService.loadBrochureById(id)
    commit('SET_BROCHURE', response.data.data)
    commit('SET_BROCHURE_FILES', response.data.data.files)
    commit('SET_BROCHURE_CATEGORIES', response.data.data.allBrochureCategories)
  },

  async loadBrochureFiles({ commit }, id) {
    const response = await PriceService.brochureFiles(id)
    console.log(response.data)
    commit('SET_BROCHURE_FILES', response.data)
  },

  async filterBrochureFiles({ commit }, data) {
    try {
      const response = await PriceService.fetchFilteredBrochure(data)

      if (response.status == HTTP_STATUS_CODES.OK) {
        commit('SET_BROCHURE_FILES', response.data.data)
        commitPaginationFromMeta(commit, response)
        commit('SET_TOTAL_BROCHURE_FILES', response.data.meta.total)
      }
    } catch (e) {
      showErrorToast(e)
      console.error(e)
    }
  },

  async loadProductDealers({ commit }) {
    const response = await ProductService.loadProductDealersUsers()
    let dealers = []

    if (response) {
      dealers = response.data.map((user) => mapDealerOption(user, { includeHashId: true }))
      commit('SET_DEALERS', dealers)
    }
  },

  async loadProductParts({ commit }) {
    const response = await ProductService.productParts()
    if (!response?.data) return

    const data = response.data.data ?? response.data
    commit('SET_CATEGORIES_SELECT', data.categories ?? [])
    commit('SET_VENDORS_SELECT', data.vendors ?? [])
    commit('SET_SUPPLIERS_SELECT', data.suppliers ?? [])
    commit('SET_PRODUCTS_SELECT', data.products ?? [])
    const statuses = data.statuses ?? []
    commit('SET_PRODUCT_STATUSES_SELECT', Array.isArray(statuses) ? statuses : [])
  }
}
